import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export function useScrollAnimation(options: UseScrollAnimationOptions = {}) {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

interface UseParallaxScrollOptions {
  speed?: number;
  direction?: 'up' | 'down';
}

export function useParallaxScroll({ 
  speed = 0.3, 
  direction = 'up' 
}: UseParallaxScrollOptions = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  // Cache element position to avoid getBoundingClientRect on every scroll (prevents forced reflow)
  const cachedTop = useRef<number | null>(null);
  const cachedHeight = useRef<number>(0);

  useEffect(() => {
    if (speed === 0) return;

    // Measure once and cache
    const measureElement = () => {
      if (!ref.current) return;
      const scrollY = window.scrollY;
      const rect = ref.current.getBoundingClientRect();
      cachedTop.current = rect.top + scrollY;
      cachedHeight.current = rect.height;
    };

    measureElement();

    // Re-measure on resize (layout may change)
    const resizeObserver = new ResizeObserver(() => {
      measureElement();
    });
    if (ref.current) resizeObserver.observe(ref.current);

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (cachedTop.current === null) { ticking = false; return; }
          
          const scrollY = window.scrollY;
          const windowHeight = window.innerHeight;
          
          // Calculate element's current viewport position from cached absolute position
          const elementTopInViewport = cachedTop.current - scrollY;
          const elementCenter = elementTopInViewport + cachedHeight.current / 2;
          const viewportCenter = windowHeight / 2;
          const distanceFromCenter = elementCenter - viewportCenter;
          
          const parallaxOffset = distanceFromCenter * speed * (direction === 'up' ? -1 : 1);
          setOffset(parallaxOffset);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [speed, direction]);

  return { ref, offset };
}

